import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

const Tooltip = ({ text, maxLength, tooltipTextLimit, isSmall = false, isRight = false, isSmallFont = false }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [tooltipRect, setTooltipRect] = useState(null);
    const triggerRef = useRef(null);

    const safeText = typeof text === 'string' ? text : '';
    const isTextTruncated = safeText.length > maxLength;
    const displayText = isTextTruncated ? safeText.substring(0, maxLength) + '...' : safeText;
    const isTooltipTextTruncated = safeText.length > (tooltipTextLimit ?? 100);
    const tooltipText = isTooltipTextTruncated ? safeText.substring(0, tooltipTextLimit ?? 100) + '...' : safeText;

    useEffect(() => {
        if (!isHovered || !isTextTruncated || !triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        setTooltipRect({
            top: rect.top,
            bottom: rect.bottom,
            left: rect.left,
            right: rect.right,
            width: rect.width,
            height: rect.height,
        });
    }, [isHovered, isTextTruncated]);

    const tooltipBoxStyles = {
        position: 'fixed',
        top: tooltipRect
            ? (isRight ? tooltipRect.top + tooltipRect.height / 2 : tooltipRect.bottom + 6)
            : 0,
        left: tooltipRect
            ? (isRight ? tooltipRect.right + 8 : tooltipRect.left + tooltipRect.width / 2)
            : 0,
        transform: isRight ? 'translateY(-50%)' : 'translateX(-50%)',
        maxWidth: isSmall ? '400px' : '400px',
        minWidth: '120px',
        backgroundColor: '#333',
        color: '#fff',
        padding: '8px 10px',
        borderRadius: '4px',
        textAlign: 'left',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
        zIndex: 9999,
        pointerEvents: 'none',
    };

    return (
        <>
            <div
                ref={triggerRef}
                style={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => { setIsHovered(false); setTooltipRect(null); }}
            >
                <span style={{ fontSize: isSmallFont ? '10px' : '14px' }}>{displayText}</span>
            </div>
            {isHovered && isTextTruncated && tooltipRect && typeof document !== 'undefined' && document.body &&
                createPortal(
                    <div style={tooltipBoxStyles} role="tooltip">
                        {tooltipText}
                    </div>,
                    document.body
                )
            }
        </>
    );
};

export default Tooltip;

// PropTypes definition
Tooltip.propTypes = {
  text: PropTypes.string.isRequired,
  maxLength: PropTypes.number.isRequired,
  tooltipTextLimit: PropTypes.number,
  isSmall: PropTypes.bool,
  isRight: PropTypes.bool,
  isSmallFont: PropTypes.bool
};

// Default props
Tooltip.defaultProps = {
  tooltipTextLimit: 100,
  isSmall: false,
  isRight: false,
  isSmallFont: false
};
